"use client"
import React from 'react'
import { motion } from "motion/react"
import { BlurFade } from '@/app/components/BlurFade'
import { MMHeader, MMTabBar, MMFooter } from '../../components/MMChrome'
import { RedirectToPC } from '@/app/components/DeviceRedirect'
import { pick, type Locale } from '@/lib/i18n'
import { mmDict } from '@/lib/i18n/dicts/mm'

// 特性图标（与文案 features 按顺序对应）
const FEATURE_ICONS = [
    'M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z',
    'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
    'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
]

export default function MMAppClient({ gameCount, locale }: { gameCount: number; locale: Locale }) {
    const t = pick(mmDict, locale).app
    const FEATURES = t.features.map((f, i) => ({
        ...f,
        desc: f.desc.replace('{count}', String(gameCount)),
        icon: FEATURE_ICONS[i],
    }))
    return (
        <div className="bg-slate-950 min-h-screen text-slate-200 max-w-md mx-auto relative shadow-2xl shadow-black">
            <style>{`
                @keyframes mm-float-app { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
            `}</style>
            <MMHeader locale={locale} />
            <RedirectToPC href="/" />

            {/* Hero */}
            <section className="relative px-5 pt-8 pb-6 overflow-hidden">
                <div className="pointer-events-none absolute -top-24 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-indigo-600/20 blur-[90px]" />
                <div className="pointer-events-none absolute top-24 -right-16 h-40 w-40 rounded-full bg-fuchsia-500/15 blur-[70px]" />
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="text-center">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500/10 ring-1 ring-indigo-400/30 px-3 py-1 text-[11px] font-medium text-indigo-300">
                        <span className="relative flex size-1.5">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex size-1.5 rounded-full bg-indigo-400"></span>
                        </span>
                        {t.badge}
                    </span>
                    <h1 className="mt-4 text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 via-white to-fuchsia-300">
                        {t.title}
                    </h1>
                    <p className="mt-2 text-xs leading-5 text-slate-400">
                        {t.desc}
                    </p>
                </motion.div>
            </section>

            {/* 下载二维码 */}
            <section className="px-4 pb-6">
                <div className="grid grid-cols-2 gap-4">
                    {[
                        { name: "Android", img: "/img/download_android.png", delay: 0.1, url: process.env.NEXT_PUBLIC_URL_APK },
                        { name: "iOS", img: "/img/download_apple.png", delay: 0.2, url: process.env.NEXT_PUBLIC_URL_IOS },
                    ].map((p) => (
                        <BlurFade key={p.name} delay={p.delay} inView>
                            <div className="rounded-2xl bg-slate-900 ring-1 ring-white/10 p-4 flex flex-col items-center transition-all duration-300 hover:ring-indigo-400/40">
                                <div className="rounded-xl bg-white p-2.5 ring-1 ring-white/10" style={{ animation: `mm-float-app 4s ease-in-out ${p.delay}s infinite` }}>
                                    <img src={p.img} alt={t.qrAlt.replace('{name}', p.name)} className="w-full h-auto rounded-md" loading="lazy" />
                                </div>
                                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-white">
                                    {p.name === "Android" ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-4 text-emerald-400">
                                            <path d="M7.2 16.8v-6h-1.6a.8.8 0 0 1-.8-.8V6.4c0-.44.36-.8.8-.8s.8.36.8.8v3.6h11.2V6.4c0-.44.36-.8.8-.8s.8.36.8.8V10a.8.8 0 0 1-.8.8h-1.6v6a1.6 1.6 0 0 1-1.6 1.6h-.8v2.4a.8.8 0 0 1-1.6 0v-2.4h-2.4v2.4a.8.8 0 0 1-1.6 0v-2.4h-.8a1.6 1.6 0 0 1-1.6-1.6ZM6.4 3.2 7.6 4.8a5.6 5.6 0 0 1 8.8 0l1.2-1.6M9.6 7.2h.8m3.2 0h.8" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" />
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-4 text-slate-200">
                                            <path d="M17.05 20.28c-.98.95-2.05.86-3.08.41-1.09-.47-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.41C2.79 15.25 3.51 7.59 9.05 7.31c1.27.07 2.15.74 2.91.78.98-.2 1.92-.87 3.22-.83 1.52.12 2.65.72 3.4 1.82-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.01 4.07ZM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25Z" />
                                        </svg>
                                    )}
                                    {p.name}
                                </span>
                                {/* 手机端直接点击下载（二维码保留给扫码场景） */}
                                <a
                                    href={p.url}
                                    target="_blank"
                                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-4 py-2.5 text-xs font-bold text-white shadow-lg shadow-indigo-600/25 active:scale-95 transition-transform"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                    </svg>
                                    {t.download}
                                </a>
                                <span className="mt-1.5 text-[10px] text-slate-500">{t.scanHint}</span>
                            </div>
                        </BlurFade>
                    ))}
                </div>
            </section>

            {/* 特性 */}
            <section className="px-4 pb-6">
                <BlurFade inView>
                    <h2 className="text-center text-xl font-black text-white">{t.whyTitle}</h2>
                </BlurFade>
                <div className="mt-5 flex flex-col gap-3">
                    {FEATURES.map((f, i) => (
                        <BlurFade key={f.title} delay={0.08 * i} inView>
                            <div className="flex items-start gap-4 rounded-2xl bg-slate-900 ring-1 ring-white/10 p-4">
                                <span className="rounded-xl bg-gradient-to-br from-indigo-500/20 to-fuchsia-500/20 ring-1 ring-indigo-400/30 p-3 text-indigo-300 shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d={f.icon} />
                                    </svg>
                                </span>
                                <div>
                                    <h3 className="text-sm font-bold text-white">{f.title}</h3>
                                    <p className="mt-1 text-xs leading-5 text-slate-400">{f.desc}</p>
                                </div>
                            </div>
                        </BlurFade>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="px-4 pb-6">
                <BlurFade inView>
                    <div className="rounded-3xl bg-gradient-to-r from-indigo-600 to-fuchsia-600 p-6 text-center shadow-xl shadow-indigo-600/25">
                        <h2 className="text-lg font-black text-white">{t.cta.title}</h2>
                        <p className="mt-2 text-xs text-indigo-100">{t.cta.desc}</p>
                        <div className="mt-4 flex justify-center gap-3">
                            <a href="/mm/contact"
                                className="inline-flex items-center justify-center rounded-full bg-white px-6 py-2.5 text-sm font-bold text-indigo-600 active:scale-95 transition-transform">
                                {t.cta.contact}
                            </a>
                            <a href="/mm/gamelist"
                                className="inline-flex items-center justify-center rounded-full ring-1 ring-white/50 px-6 py-2.5 text-sm font-medium text-white active:scale-95 transition-transform">
                                {t.cta.browse}
                            </a>
                        </div>
                    </div>
                </BlurFade>
            </section>

            <MMFooter locale={locale} />
            <MMTabBar active="app" locale={locale} />
        </div>
    )
}
